import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleCopyUrl,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  FieldTable,
  Input,
  RadioButton,
  Select,
  TextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function COBSFTSessionLogv2() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [isIndividualsModalOpen, setIsIndividualsModalOpen] = useState(false);
  const [selectedIndividuals, setSelectedIndividuals] = useState([]);

  const customValidate = (errs) => {
    if (getFieldValue(data.ProgressNotesNA) !== "true") {
      const progressNotes = JSON.parse(data.ProgressNotes["@value"]);

      if (progressNotes.length === 0) {
        if (errs) errs.ProgressNotes = "Progress Notes are required.";
        else
          errs = {
            ProgressNotes: "Progress Notes are required.",
          };
      } else {
        const notesWithoutAllFields = progressNotes.filter(
          (detail) =>
            detail.Interaction === undefined ||
            detail.Interaction === "" ||
            detail.Highlight === undefined ||
            detail.Highlight === "" ||
            detail.Reframe === undefined ||
            detail.Reframe === "" ||
            detail.Task === undefined ||
            detail.Task === "" ||
            detail.Changes === undefined ||
            detail.Changes === ""
        );

        if (notesWithoutAllFields.length > 0) {
          if (errs)
            errs.ProgressNotes =
              "All fields are required for each Progress Note.";
          else
            errs = {
              ProgressNotes: "All fields are required for each Progress Note.",
            };
        } else {
          delete errs.ProgressNotes;
        }
      }

      console.log("CustomErrors: ", errs);
    }

    setErrors(errs);
    return errs;
  };

  const getInteractionOptions = (diagnoses) => {
    if (!diagnoses) {
      return [];
    }

    const options = JSON.parse(diagnoses).map((item) => {
      return { _id: item.CodeValue, name: item.CodeText };
    });

    return options;
  };

  const contactTypeOptions = [
    { _id: "Therapy Session", name: "Therapy Session" },
    { _id: "Telephone", name: "Telephone" },
    { _id: "Cancellation/No Show", name: "Cancellation/No Show" },
    { _id: "Other", name: "Other" },
  ];

  const focusOptions = [
    { _id: "None", name: "None" },
    { _id: "Minimal", name: "Minimal" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "Considerable", name: "Considerable" },
    { _id: "Extensive", name: "Extensive" },
  ];

  const locationOptions = [
    { _id: "Telephone", name: "Telephone" },
    { _id: "Clinic/Agency", name: "Clinic/Agency" },
    { _id: "Home", name: "Home" },
    {
      _id: "Friend or Relative's Residence",
      name: "Friend or Relative's Residence",
    },
    { _id: "School", name: "School" },
  ];

  const severityOptions = [
    {
      _id: "Most severe - Interactions are rigid, inflexible, are continuously present, and severely impair functioning.",
      name: "Most severe",
      title:
        "Interactions are rigid, inflexible, are continuously present, and severely impair functioning.",
    },
    {
      _id: "Moderately Severe - Interactions are typically rigid, inflexible, are present most of the time, and typically impair functioning.",
      name: "Moderately Severe",
      title:
        "Interactions are typically rigid, inflexible, are present most of the time, and typically impair functioning.",
    },
    {
      _id: "Moderate - Rigid, inflexible interactions are noticeable and frequently interfere with functioning, but not dominate the character of the interactions.",
      name: "Moderate",
      title:
        "Rigid, inflexible interactions are noticeable and frequently interfere with functioning, but not dominate the character of the interactions.",
    },
    {
      _id: "Mild - Problematic interactions occasionally present but do not interfere with functioning.",
      name: "Mild",
      title:
        "Problematic interactions occasionally present but do not interfere with functioning.",
    },
    {
      _id: "Satisfactory - Problematic interactions seldom present and rarely interfere with functioning.",
      name: "Satisfactory",
      title:
        "Problematic interactions seldom present and rarely interfere with functioning.",
    },
    {
      _id: "Excellent - Problematic interactions are rarely present and do not interfere with functioning.",
      name: "Excellent",
      title:
        "Problematic interactions are rarely present and do not interfere with functioning.",
    },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const diagnosticCodes = {
    codeTypes: [
      { _id: "Structure", name: "Structure" },
      { _id: "Resonance", name: "Resonance" },
      { _id: "Developmental Stage", name: "Developmental Stage" },
      { _id: "Identified Patient Hood", name: "Identified Patient Hood" },
      { _id: "Conflict Resolution", name: "Conflict Resolution" },
    ],
    codeValues: {
      "Structure": [
        {
          _id: "S1",
          name: "S1) Hierarchy - unbalanced leadership (1 parent much more active than the other-note who)",
        },
        {
          _id: "S2",
          name: "S2) Hierarchy - a child is more powerful than parents",
        },
        {
          _id: "S3",
          name: "S3) Behavior Control - parents not engaging in behavior control when needed",
        },
        {
          _id: "S4",
          name: "S4) Behavior Control - parents engaging in ineffective behavior control e.g. inappropriate consequences, lack of follow-through, unclear expectations, inconsistency, overemotional",
        },
        {
          _id: "S5",
          name: "S5) Guidance/Nurturance - lack of nurturing behaviors from parents",
        },
        {
          _id: "S6",
          name: "S6) Guidance/Nurturance - lack of guiding behaviors from parents",
        },
        {
          _id: "S7",
          name: "S7) Guidance/Nurturance - parents are poor role models, e.g. engaged in illegal activity, substance abuser, violent (note who)",
        },
        {
          _id: "S8",
          name: "S8) Parental Alliance - parents not working together in parenting",
        },
        {
          _id: "S9",
          name: "S9) Spousal Alliance - poor marital relationship (high conflict or disengagement)",
        },
        {
          _id: "S10",
          name: "S10) Executive Subsystem - absence of decision-making subsystem",
        },
        {
          _id: "S11",
          name: "S11) Sibling Subsystem - poor sibling relationship (high conflict or disengagement)",
        },
        {
          _id: "S12",
          name: "S12) Triangulation - child triangulated in conflict between adults",
        },
        {
          _id: "S13",
          name: "S13) Communication - lack of direct verbal communication between each pair of family members (indicate dyad that does not communicate)",
        },
        {
          _id: "S14",
          name: "S14) Communication - a member of the family serves as a switchboard operator (note who)",
        },
        {
          _id: "S15",
          name: "S15) Communication - ineffective communication, e.g. vagueness, sermonizing, overemotional",
        },
      ],
      "Resonance": [
        {
          _id: "R1",
          name: "R1) Enmeshment - Lack of physical boundaries; e.g. privacy invasions, violence (note dyad(s))",
        },
        {
          _id: "R2",
          name: "R2) Enmeshment - Lack of emotional boundaries; e.g. mind reading. Un-differentiation, conflict not dealt with due to concerns of hurting each other, differentness not tolerated, outsiders not tolerated (not dyad(s))",
        },
        {
          _id: "R3",
          name: "R3) Disengagement - Physical distance; e.g. absence from home, lack of participation in family activities, lack of communication (note who)",
        },
        {
          _id: "R4",
          name: "R4) Disengagement - Emotional distance; e.g. not allied with other family members, not affectively involved, not aware of emotional life or life issues of other family members (note who)",
        },
      ],
      "Developmental Stage": [
        { _id: "D1", name: "D1) Immature parenting" },
        {
          _id: "D2",
          name: "D2) Child treated as/acts too young; e.g. overly restrictive, low requirements/opportunity for responsible behavior, no negotiation allowed",
        },
        { _id: "D3", name: "D3) Child overloaded with adult tasks" },
        {
          _id: "D4",
          name: "D4) Extended family usurps parental power or otherwise treats parent like child",
        },
      ],
      "Identified Patient Hood": [
        { _id: "I1", name: "I1) Negativity about the IP" },
        { _id: "I2", name: "I2) Centrality of IP" },
        { _id: "I3", name: "I3) Overprotection or Nurturance of IPhood" },
        { _id: "I4", name: "I4) Denial of other problems" },
        { _id: "I5", name: "I5) Lack of interactive perspective" },
      ],
      "Conflict Resolution": [
        { _id: "C1", name: "C1) Denial" },
        { _id: "C2", name: "C2) Avoidance" },
        { _id: "C3", name: "C3) Diffusion" },
        { _id: "C4", name: "C4) Emergence without resolution" },
        {
          _id: "C5",
          name: "C5) Lack of appropriate prioritization of conflicts (i.e., knowing which things are worth arguing over)",
        },
      ],
    },
    codePlans: {
      S1: "Make leadership more balanced between parents.",
      S2: "Elevates parents to higher position in hierarchy.",
      S3: "Get parents to recognize when behavior control is needed and act accordingly.",
      S4: "Improve parents behavior control behaviors so that they are effective.",
      S5: "Increase nurturing behaviors from parents.",
      S6: "Increase guiding behaviors from parents.",
      S7: "Elevate parent so that he/she recognizes his/her responsibility as a role model, get parents to participate in outside treatment if appropriate, engage child and parent with additional adult figures in natural environment.",
      S8: "Get parents to increase cooperation in parenting.",
      S9: "Improve marital relationship to the extent necessary for cooperative parenting.",
      S10: "Get family to define and put into effect a decision-making subsystem.",
      S11: "Improve sibling relationship.",
      S12: "Extract child from conflict between adults.",
      S13: "Get each dyad to communicate directly without need for intermediary.",
      S14: "Get each dyad to communicate directly without need for intermediary.",
      S15: "Improve communication style so that issues can be discussed effectively.",
      R1: "Get family to accept and implement physical boundaries to provide for privacy, implement rule regarding violence if needed.",
      R2: "Get family to implement emotional boundaries so that each individuals need for differentiation are met and so that conflict can be dealt with effectively.",
      R3: "Get disengaged person to have more contact, communication and participation in family activities.",
      R4: "Get disengaged person to have more affective involvement with family.",
      D1: "Elevate parent so that he/she recognizes his/her responsibility as a parent, get parent to participate in outside treatment or parenting education if appropriate, engage child and parent with additional adult figures in natural environment.",
      D2: "Get parents to recognize need for developmental adjustment and treat child in a more age-appropriate manner/get child to behave in more mature and responsible manner.",
      D3: "Eet others (either in family or through outside resources) to take over some of these adults tasks and/or get child to better cope with tasks.",
      D4: "Elevate parent and help to create a boundary from extended family that still allows for support.",
      I1: "Get family to to decrease negativity statements and increase positive statements about the IP.",
      I2: "De-centralize IP.",
      I3: "Get family to confront and discourage IPhood.",
      I4: "Get family to recognize problems behavior of other family members and place some blame on contextual stressors for the problematic behavior of the IP (while still maintaining view that IP has responsibility).",
      I5: "Get family to recognize and act upon the knowledge that the behavior of others in the family have an impact on the behavior of the IP.",
      C1: "Get family to allow discussion of problems and conflicts to emerge and then resolve them effectively.",
      C2: "Get family to sustain discussion of problems and conflicts and then resolve them effectively.",
      C3: "Get family to stay on the problems or conflict at hand and then resolve conflicts effectively.",
      C4: "Get family to generate solution to conflict or problems and then reach a decision.",
      C5: "Get family to set effective priorities for which conflicts or problems they should address.",
    },
  };

  const individualsNeedingInitials = [
    "Sister",
    "Brother",
    "Live-in Partner",
    "Foster Parent/Guardian",
    "Grandmother",
    "Grandfather",
    "Cousin",
    "Aunt",
    "Uncle",
    "Other Extended Family",
    "Peer",
    "School Rep",
    "Juvenile Justice Rep",
    "Other",
  ];

  const handleSelectIndividuals = () => {
    const obj = { ...data };
    obj.IndividualsPresent["@value"] = selectedIndividuals.sort().map((s) => {
      return `${
        individualsNeedingInitials.includes(s) ? `${s} - initials: ` : `${s}`
      }`;
    }).join(`
`);
    setData(obj);
    setIsIndividualsModalOpen(false);
  };

  ////UI
  return (
    <>
      {isIndividualsModalOpen ? (
        <Modal
          className="rounded"
          show={isIndividualsModalOpen}
          onHide={() => {}}
          animation={false}
          size="lg"
        >
          <Modal.Header className="bg-safy3-1">
            <Modal.Title>Individuals Present</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Adolescent"
                    label="Adolescent - IP"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Adolescent - IP")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Adolescent - IP")) {
                        selected = selected.filter(
                          (s) => s !== "Adolescent - IP"
                        );
                      } else {
                        selected.push("Adolescent - IP");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Grandfather"
                    label="Grandfather"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Grandfather")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Grandfather")) {
                        selected = selected.filter((s) => s !== "Grandfather");
                      } else {
                        selected.push("Grandfather");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Mother"
                    label="Mother (biological or adoptive)"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Mother")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Mother")) {
                        selected = selected.filter((s) => s !== "Mother");
                      } else {
                        selected.push("Mother");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Cousin"
                    label="Cousin"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Cousin")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Cousin")) {
                        selected = selected.filter((s) => s !== "Cousin");
                      } else {
                        selected.push("Cousin");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Father"
                    label="Father (biological or adoptive)"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Father")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Father")) {
                        selected = selected.filter((s) => s !== "Father");
                      } else {
                        selected.push("Father");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Aunt"
                    label="Aunt"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Aunt")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Aunt")) {
                        selected = selected.filter((s) => s !== "Aunt");
                      } else {
                        selected.push("Aunt");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Sister"
                    label="Sister"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Sister")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Sister")) {
                        selected = selected.filter((s) => s !== "Sister");
                      } else {
                        selected.push("Sister");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Uncle"
                    label="Uncle"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Uncle")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Uncle")) {
                        selected = selected.filter((s) => s !== "Uncle");
                      } else {
                        selected.push("Uncle");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Brother"
                    label="Brother"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Brother")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Brother")) {
                        selected = selected.filter((s) => s !== "Brother");
                      } else {
                        selected.push("Brother");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="OtherExtendedFamily"
                    label="Other Extended Family"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes(
                      "Other Extended Family"
                    )}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Other Extended Family")) {
                        selected = selected.filter(
                          (s) => s !== "Other Extended Family"
                        );
                      } else {
                        selected.push("Other Extended Family");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Stepmother"
                    label="Stepmother"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Stepmother")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Stepmother")) {
                        selected = selected.filter((s) => s !== "Stepmother");
                      } else {
                        selected.push("Stepmother");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Peer"
                    label="Peer"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Peer")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Peer")) {
                        selected = selected.filter((s) => s !== "Peer");
                      } else {
                        selected.push("Peer");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Stepfather"
                    label="Stepfather"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Stepfather")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Stepfather")) {
                        selected = selected.filter((s) => s !== "Stepfather");
                      } else {
                        selected.push("Stepfather");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="SchoolRep"
                    label="School Rep"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("School Rep")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("School Rep")) {
                        selected = selected.filter((s) => s !== "School Rep");
                      } else {
                        selected.push("School Rep");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="LiveinPartner"
                    label="Live-in Partner"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Live-in Partner")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Live-in Partner")) {
                        selected = selected.filter(
                          (s) => s !== "Live-in Partner"
                        );
                      } else {
                        selected.push("Live-in Partner");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="JuvenileJusticeRep"
                    label="Juvenile Justice Rep"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes(
                      "Juvenile Justice Rep"
                    )}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Juvenile Justice Rep")) {
                        selected = selected.filter(
                          (s) => s !== "Juvenile Justice Rep"
                        );
                      } else {
                        selected.push("Juvenile Justice Rep");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="FosterParentGuardian"
                    label="Foster Parent/Guardian"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes(
                      "Foster Parent/Guardian"
                    )}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Foster Parent/Guardian")) {
                        selected = selected.filter(
                          (s) => s !== "Foster Parent/Guardian"
                        );
                      } else {
                        selected.push("Foster Parent/Guardian");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="Other"
                    label="Other"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Other")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Other")) {
                        selected = selected.filter((s) => s !== "Other");
                      } else {
                        selected.push("Other");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col pt-2">
                  <Checkbox
                    name="Grandmother"
                    label="Grandmother"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("Grandmother")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("Grandmother")) {
                        selected = selected.filter((s) => s !== "Grandmother");
                      } else {
                        selected.push("Grandmother");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col pt-2">
                  <Checkbox
                    name="NA"
                    label="N/A"
                    divClassName="ms-3"
                    checked={selectedIndividuals.includes("N/A")}
                    onChange={() => {
                      let selected = [...selectedIndividuals];

                      if (selected.includes("NA")) {
                        selected = selected.filter((s) => s !== "N/A");
                      } else {
                        selected.push("N/A");
                      }

                      setSelectedIndividuals(selected);
                    }}
                    disabled={formDisabled}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-primary"
              onClick={handleSelectIndividuals}
            >
              Select
            </button>
            <button
              className="btn btn-danger"
              onClick={() => {
                setIsIndividualsModalOpen(false);
              }}
            >
              Cancel
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <div className="h5">Contact Log</div>
              <div className="row">
                <div className="col">
                  <Input
                    name="SessionDate"
                    label="Date of Session:"
                    error={errors.SessionDate}
                    value={getFieldValue(data.SessionDate)}
                    onChange={handleChange}
                    identical={compare("SessionDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <Input
                    name="SessionTime"
                    label="Time:"
                    error={errors.SessionTime}
                    value={getFieldValue(data.SessionTime)}
                    onChange={handleChange}
                    identical={compare("SessionTime")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="SessionLength"
                    label="Length:"
                    error={errors.SessionLength}
                    value={getFieldValue(data.SessionLength)}
                    onChange={handleChange}
                    identical={compare("SessionLength")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Select
                    name="ContactType"
                    label="Type of Contact:"
                    error={errors.ContactType}
                    value={getFieldValue(data.ContactType)}
                    options={contactTypeOptions}
                    onChange={handleChange}
                    identical={compare("ContactType")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  Individuals Present:
                  <br />
                  <button
                    className="btn btn-primary float-start mt-2"
                    onClick={() => {
                      setIsIndividualsModalOpen(true);
                    }}
                  >
                    Quick Select
                  </button>
                </div>
                <div className="col">
                  <Select
                    name="SessionLocation"
                    label="Location:"
                    error={errors.SessionLocation}
                    value={getFieldValue(data.SessionLocation)}
                    options={locationOptions}
                    onChange={handleChange}
                    identical={compare("SessionLocation")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <Input
                name="IndividualsPresent"
                label="Individual(s) details"
                error={errors.IndividualsPresent}
                value={getFieldValue(data.IndividualsPresent)}
                onChange={handleChange}
                identical={compare("IndividualsPresent")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                placeholder="Use initials"
              />
              <RadioButton
                name="ExpectedInAttendance"
                label="Was everyone expected to be present in attendance?"
                error={errors.ExpectedInAttendance}
                value={getFieldValue(data.ExpectedInAttendance)}
                options={yesNoOptions}
                onChange={handleChange}
                identical={compare("ExpectedInAttendance")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.ExpectedInAttendance) !== "Yes" ? (
                <Input
                  name="IndividualsNotPresent"
                  label="Who was not present? Use initials"
                  error={errors.IndividualsNotPresent}
                  value={getFieldValue(data.IndividualsNotPresent)}
                  onChange={handleChange}
                  identical={compare("IndividualsNotPresent")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  placeholder="Use initials"
                />
              ) : null}
              <Input
                name="Therapist"
                label="Therapist:"
                error={errors.Therapist}
                value={getFieldValue(data.Therapist)}
                onChange={handleChange}
                identical={compare("Therapist")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Videotaped"
                label="Videotaped and sent to Supervisor?"
                error={errors.Videotaped}
                value={getFieldValue(data.Videotaped)}
                options={yesNoOptions}
                onChange={handleChange}
                identical={compare("Videotaped")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.Videotaped) === "Yes" ? (
                <>
                  <div className="h5">Viedotape</div>
                  <Input
                    name="SessionNumber"
                    label="Session Number:"
                    error={errors.SessionNumber}
                    value={getFieldValue(data.SessionNumber)}
                    onChange={handleChange}
                    identical={compare("SessionNumber")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="SeatingMap"
                    label="Seating Map:"
                    error={errors.SeatingMap}
                    value={getFieldValue(data.SeatingMap)}
                    onChange={handleChange}
                    identical={compare("SeatingMap")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="AdditionalNotes"
                    label="Comments and Context of the Session:"
                    error={errors.AdditionalNotes}
                    value={getFieldValue(data.AdditionalNotes)}
                    onChange={handleChange}
                    identical={compare("AdditionalNotes")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              {getFieldValue(data.ContactType) === "Therapy Session" ? (
                <>
                  <div className="h5">Weekly Session Summary</div>
                  <Input
                    name="PresentingProblem"
                    label="Presenting Problem:"
                    error={errors.PresentingProblem}
                    value={getFieldValue(data.PresentingProblem)}
                    onChange={handleChange}
                    identical={compare("PresentingProblem")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="SessionNumber"
                    label="Session Number:"
                    error={errors.SessionNumber}
                    value={getFieldValue(data.SessionNumber)}
                    onChange={handleChange}
                    identical={compare("SessionNumber")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="NextSessionScheduled"
                    label="Next Session Scheduled:"
                    error={errors.NextSessionScheduled}
                    value={getFieldValue(data.NextSessionScheduled)}
                    options={yesNoOptions}
                    onChange={handleChange}
                    identical={compare("NextSessionScheduled")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.NextSessionScheduled) !== "Yes" ? (
                    <Input
                      name="NotScheduledDetails"
                      label="If not, why?"
                      error={errors.NotScheduledDetails}
                      value={getFieldValue(data.NotScheduledDetails)}
                      onChange={handleChange}
                      identical={compare("NotScheduledDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <FieldTable name="Diagnoses" commentsHandler={handleComments}>
                    <div className="container border-start border-top border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Diagnostic Code
                        </div>
                        <div className="col border-end py-2">
                          Level of Severity
                        </div>
                        <div className="col-2 border-end py-2">Focus</div>
                        <div className="col-4 py-2">
                          Treatment Plan for the Family
                        </div>
                      </div>
                      {data.Diagnoses["@value"] !== undefined
                        ? JSON.parse(getFieldValue(data.Diagnoses)).map(
                            (item, index) => {
                              return (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(index, "Diagnoses");
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(index, "Diagnoses");
                                          }}
                                        ></i>
                                        <Select
                                          name="CodeType"
                                          value={item.CodeType}
                                          options={diagnosticCodes.codeTypes}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "Diagnoses"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.CodeType !== undefined &&
                                        item.CodeType !== "" ? (
                                          <Select
                                            name="CodeValue"
                                            value={item.CodeValue}
                                            options={
                                              diagnosticCodes.codeValues[
                                                item.CodeType
                                              ]
                                            }
                                            onChange={(e) => {
                                              console.log(e);
                                              const codeValue =
                                                e.currentTarget.value;

                                              handleRowChange(
                                                e,
                                                index,
                                                "Diagnoses"
                                              );

                                              handleRowChange(
                                                {
                                                  currentTarget: {
                                                    name: "CodeText",
                                                    value:
                                                      diagnosticCodes.codeValues[
                                                        item.CodeType
                                                      ].filter(
                                                        (cv) =>
                                                          cv._id === codeValue
                                                      )[0].name,
                                                    type: "input",
                                                  },
                                                },
                                                index,
                                                "Diagnoses"
                                              );

                                              handleRowChange(
                                                {
                                                  currentTarget: {
                                                    name: "TreatmentPlan",
                                                    value:
                                                      diagnosticCodes.codePlans[
                                                        codeValue
                                                      ],
                                                    type: "input",
                                                  },
                                                },
                                                index,
                                                "Diagnoses"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(index, "Diagnoses");
                                          }}
                                        ></i>
                                        {item.CodeText}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Select
                                        name="Severity"
                                        value={item.Severity}
                                        options={severityOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "Diagnoses"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Severity
                                    )}
                                  </div>
                                  <div className="col-2 border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Select
                                        name="Focus"
                                        value={item.Focus}
                                        options={focusOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "Diagnoses"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Focus
                                    )}
                                  </div>
                                  <div className="col-4 py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <TextArea
                                        name="TreatmentPlan"
                                        value={item.TreatmentPlan}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "Diagnoses"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.TreatmentPlan
                                    )}
                                  </div>
                                </div>
                              );
                            }
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("Diagnoses", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Diagnostic Code
                        </button>
                      </>
                    ) : null}
                    {errors.Diagnoses !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.Diagnoses}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </FieldTable>
                  <div className="d-flex">
                    <span className="h5">Progress Note</span>{" "}
                    <Checkbox
                      name="ProgressNotesNA"
                      label="N/A; Engaging in initial sessions"
                      divClassName="ms-3"
                      checked={getFieldValue(data.ProgressNotesNA) === "true"}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  {getFieldValue(data.ProgressNotesNA) === "true" ? (
                    <Input
                      name="SessionDescription"
                      label="Description of Session, including techniques, interventions, and client/family response. For initial sessions, this should include joining, developing enactments and diagnosing interactions."
                      error={errors.SessionDescription}
                      value={getFieldValue(data.SessionDescription)}
                      onChange={handleChange}
                      identical={compare("SessionDescription")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : (
                    <>
                      <FieldTable
                        name="ProgressNotes"
                        commentsHandler={handleComments}
                      >
                        {data.ProgressNotes["@value"] !== undefined ? (
                          <ol type="A">
                            {JSON.parse(getFieldValue(data.ProgressNotes)).map(
                              (item, index) => {
                                return (
                                  <li key={item._id}>
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "ProgressNotes"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "ProgressNotes"
                                            );
                                          }}
                                        ></i>
                                        <Select
                                          name="Interaction"
                                          label="Problematic Interaction:"
                                          value={item.Interaction}
                                          options={getInteractionOptions(
                                            getFieldValue(data.Diagnoses)
                                          )}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ProgressNotes"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <ol type="a">
                                          <li>
                                            <div className="mb-2">
                                              Way they were approached in the
                                              session:
                                            </div>
                                            <ol>
                                              <li>
                                                <Input
                                                  name="Highlight"
                                                  label="Highlight:"
                                                  value={item.Highlight}
                                                  onChange={(e) => {
                                                    handleRowChange(
                                                      e,
                                                      index,
                                                      "ProgressNotes"
                                                    );
                                                  }}
                                                  disabled={formDisabled}
                                                />
                                              </li>
                                              <li>
                                                <Input
                                                  name="Reframe"
                                                  label="Reframe:"
                                                  value={item.Reframe}
                                                  onChange={(e) => {
                                                    handleRowChange(
                                                      e,
                                                      index,
                                                      "ProgressNotes"
                                                    );
                                                  }}
                                                  disabled={formDisabled}
                                                />
                                              </li>
                                              <li>
                                                <Input
                                                  name="Task"
                                                  label="Task:"
                                                  value={item.Task}
                                                  onChange={(e) => {
                                                    handleRowChange(
                                                      e,
                                                      index,
                                                      "ProgressNotes"
                                                    );
                                                  }}
                                                  disabled={formDisabled}
                                                />
                                              </li>
                                            </ol>
                                          </li>
                                          <li>
                                            <Input
                                              name="Changes"
                                              label="Any deterioration or improvements?"
                                              value={item.Changes}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ProgressNotes"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </li>
                                        </ol>
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "ProgressNotes"
                                            );
                                          }}
                                        ></i>
                                        Problematic Interaction:{" "}
                                        {item.Interaction}
                                        <ol type="a">
                                          <li>
                                            Way they were approached in the
                                            session:
                                            <ol>
                                              <li>
                                                Highlight: {item.Highlight}
                                              </li>
                                              <li>Reframe: {item.Reframe}</li>
                                              <li>Task: {item.Task}</li>
                                            </ol>
                                          </li>
                                          <li>
                                            Any deterioration or improvements?{" "}
                                            {item.Changes}
                                          </li>
                                        </ol>
                                      </>
                                    )}
                                  </li>
                                );
                              }
                            )}
                          </ol>
                        ) : null}
                        {!formDisabled ? (
                          <>
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("ProgressNotes", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Interaction
                            </button>
                          </>
                        ) : null}
                        {errors.ProgressNotes !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.ProgressNotes}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </FieldTable>
                      <Input
                        name="AdditionalComments"
                        label="Any Additional Comments:"
                        error={errors.AdditionalComments}
                        value={getFieldValue(data.AdditionalComments)}
                        onChange={handleChange}
                        identical={compare("AdditionalComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare && (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default COBSFTSessionLogv2;
